import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * <ThemeMenuItem />
 * PRIMARY placement — drop into the avatar dropdown in Dashboard.js.
 * Styled to match existing menu rows (8×8 icon chip + label + right-side badge).
 * Click behavior: CYCLE Light → Dark → System → Light.
 */
export const ThemeMenuItem = () => {
  const { theme, resolvedTheme, setTheme } = useTheme();

  const cycle = () => {
    const next =
      theme === 'light' ? 'dark' :
      theme === 'dark'  ? 'system' :
                          'light';
    setTheme(next);
  };

  const Icon = resolvedTheme === 'dark' ? Moon : Sun;
  const label =
    theme === 'light'  ? 'Light'  :
    theme === 'dark'   ? 'Dark'   :
                         'System';

  return (
    <button
      onClick={cycle}
      data-testid="theme-menu-item"
      aria-label={`Appearance: currently ${label}. Click to cycle.`}
      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm
                 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5
                 rounded-xl transition-all duration-150"
    >
      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
      </div>
      <span className="font-medium">Appearance</span>
      <span
        data-testid="theme-menu-badge"
        className="ml-auto text-xs text-slate-400 dark:text-slate-500 tabular-nums"
      >
        {label}
      </span>
    </button>
  );
};

/**
 * <ThemeToggle />
 * SECONDARY placement — Settings → Appearance page.
 * 3-option radio-style pill group.
 */
export const ThemeToggle = ({ className = '' }) => {
  const { theme, setTheme } = useTheme();

  const options = [
    { value: 'light',  label: 'Light',  Icon: Sun     },
    { value: 'dark',   label: 'Dark',   Icon: Moon    },
    { value: 'system', label: 'System', Icon: Monitor },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      data-testid="theme-toggle-group"
      className={`inline-flex items-center gap-1 p-1 rounded-xl
                  bg-slate-100 dark:bg-white/5
                  border border-slate-200 dark:border-white/10 ${className}`}
    >
      {options.map(({ value, label, Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            role="radio"
            aria-checked={active}
            data-testid={`theme-option-${value}`}
            onClick={() => setTheme(value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                        text-xs font-medium transition-all duration-150
                        ${active
                          ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
};
